'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { statsData } from '@/data/stats';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

interface StatsCounterProps {
  bgColor?: string;
  numberColor?: string;
  labelColor?: string;
  borderColor?: string;
}

export const StatsCounter: React.FC<StatsCounterProps> = ({
  bgColor = 'bg-[#000000]',
  numberColor = '#C89B3C',
  labelColor = '#E0E0E0',
  borderColor = 'rgba(255, 255, 255, 0.15)',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const numberRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useGSAP(
    () => {
      if (!containerRef.current) return;

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });

      timeline.fromTo(
        containerRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
      );

      statsData.forEach((stat, index) => {
        const numElement = numberRefs.current[index];
        if (!numElement) return;

        const counter = { val: 0 };

        timeline.to(
          counter,
          {
            val: stat.value,
            duration: 1.8,
            ease: 'power2.out',
            onUpdate: () => {
              numElement.textContent = Math.floor(counter.val).toLocaleString();
            },
          },
          '-=1.5'
        );
      });
    },
    { scope: containerRef }
  );

  return (
    <section 
      aria-label="Company Key Statistics"
      className="w-full px-3 xs:px-4 py-4 sm:py-6 md:py-8 flex justify-center bg-white"
    >
      <div
        ref={containerRef}
        className={`w-full max-w-[1400px] ${bgColor} rounded-lg shadow-lg overflow-hidden py-4 xs:py-6 md:py-8 px-2 xs:px-4 sm:px-6`}
      >
        {/* Responsive Grid leveraging custom xs (375px+) */}
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 w-full gap-y-6 lg:gap-y-0">
          {statsData.map((stat, index) => {
            const isLast = index === statsData.length - 1;

            return (
              <div
                key={stat.id}
                className="flex flex-col items-center justify-center text-center px-2 xs:px-3 relative"
                style={{
                  borderRight: !isLast ? `1px solid ${borderColor}` : 'none',
                }}
              >
                <div className="flex items-center text-xl xs:text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight">
                  <span
                    ref={(el) => {
                      numberRefs.current[index] = el;
                    }}
                    style={{ color: numberColor }}
                  >
                    0
                  </span>
                  <span style={{ color: numberColor }}>{stat.suffix}</span>
                </div>

                <p
                  className="text-[11px] xs:text-[12px] sm:text-[13px] md:text-[14px] font-medium mt-1 leading-snug"
                  style={{ color: labelColor }}
                >
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;